import React from 'react';
import {useTranslation} from 'react-i18next';

export interface FaqContent {
  answer: JSX.Element | string;
  id: string;
  isTop?: boolean; // top questions to be featured on homepage
  question: JSX.Element | string;
}

/**
 * 枚举值即 URL slug（/faq/<slug>）。i18n 改造时由中文值改为英文 slug，
 * 旧中文 URL 的兼容见 Faq/index.tsx 的 LEGACY_FILTER_SLUGS 映射。
 */
export enum FaqFilterType {
  all = 'all',
  how = 'how',
  imageedit = 'imageedit',
  projects = 'projects',
  howTo = 'howTo',
}

export const faqFilters: {
  [key: string]: {
    key: FaqFilterType;
  };
} = {
  [FaqFilterType.all]: {
    key: FaqFilterType.all,
  },
  [FaqFilterType.how]: {
    key: FaqFilterType.how,
  },
  [FaqFilterType.imageedit]: {
    key: FaqFilterType.imageedit,
  },
  [FaqFilterType.projects]: {
    key: FaqFilterType.projects,
  },
  [FaqFilterType.howTo]: {
    key: FaqFilterType.howTo,
  },
};

/** 分组显示名走 faq 词典（i18n），key 与枚举 slug 对齐 */
export const useFaqFilterLabel = (): {[key in FaqFilterType]: string} => {
  const {t} = useTranslation('faq');
  return {
    [FaqFilterType.all]: t('filters.all'),
    [FaqFilterType.how]: t('filters.how'),
    [FaqFilterType.imageedit]: t('filters.imageedit'),
    [FaqFilterType.projects]: t('filters.projects'),
    [FaqFilterType.howTo]: t('filters.howTo'),
  };
};

export type TopicQuestionAndAnswers = {
  topic: Exclude<FaqFilterType, FaqFilterType.all>;
  content: FaqContent[];
};

/**
 * 中文问答内容。原 imageedit/projects/howTo 三组为 fork 源项目（thenewboston）
 * 的遗留英文内容（含 /wallet、/social 等死链），与站点无关，i18n 改造时已删除。
 */
const faqQuestionsAndAnswersZh: TopicQuestionAndAnswers[] = [
  {
    content: [
      {
        answer:
          '点击页面上随处可见的”开始使用“或者功能按钮，即可跳转到注册页面，使用手机号和验证码完成注册后使用，包含图片翻译，文本翻译，图片网盘，水印，图片去文字等功能,简单易上手，注册即送大量免费额度！',
        id: 'how-to-register',
        question: '如何注册使用？',
      },
      {
        answer: (
          <span>
            象寄图片翻译在将源语言文字翻译为目标语言文字过程中，使用第三方文本翻译引擎，为了提高翻译准确度，象寄现已经接入了Papago(中韩翻译推荐)，谷歌翻译（英文拉丁语系等推荐使用),
            阿里翻译（电商版本），DeepL（小语种推荐使用).
          </span>
        ),
        id: 'how-are-coins-minted',
        isTop: true,
        question: '象寄支持谷歌翻译吗？',
      },
    ],
    topic: FaqFilterType.how,
  },
];

/** 英文问答内容（i18n） */
const faqQuestionsAndAnswersEn: TopicQuestionAndAnswers[] = [
  {
    content: [
      {
        answer:
          'Click any "Get Started" or feature button on the site to go to the sign-up page. Register with your phone number and a verification code to access image translation, text translation, cloud image storage, watermarking, text removal and more — easy to use, with plenty of free credits upon registration!',
        id: 'how-to-register',
        question: 'How do I sign up?',
      },
      {
        answer: (
          <span>
            Xiangji uses third-party text translation engines when translating source text into target languages. To
            improve accuracy, Xiangji has integrated Papago (recommended for Chinese-Korean), Google Translate
            (recommended for English and Latin-script languages), Alibaba Translation (e-commerce edition) and DeepL
            (recommended for less common languages).
          </span>
        ),
        id: 'how-are-coins-minted',
        isTop: true,
        question: 'Does Xiangji support Google Translate?',
      },
    ],
    topic: FaqFilterType.how,
  },
];

/** 한국어 질문과 답변 (2026-09-17 AI 번역) */
const faqQuestionsAndAnswersKo: TopicQuestionAndAnswers[] = [
  {
    content: [
      {
        answer:
          '페이지 어디에서나 "시작하기" 또는 기능 버튼을 클릭하면 회원가입 페이지로 이동합니다. 휴대폰 번호와 인증번호로 가입하면 이미지 번역, 텍스트 번역, 이미지 클라우드, 워터마크, 이미지 글자 제거 등의 기능을 바로 사용할 수 있어요. 사용이 간단하고, 가입 시 많은 무료 크레딧을 드립니다!',
        id: 'how-to-register',
        question: '회원가입은 어떻게 하나요?',
      },
      {
        answer: (
          <span>
            Xiangji는 원본 언어를 목표 언어로 번역할 때 제3자 텍스트 번역 엔진을 사용합니다. 번역 정확도를 높이기 위해
            Papago(중한 번역 권장), Google 번역(영어·라틴 문자 언어 권장), 알리바바 번역(이커머스 에디션), DeepL(저빈도
            언어 권장)을 연동했습니다.
          </span>
        ),
        id: 'how-are-coins-minted',
        isTop: true,
        question: 'Xiangji는 Google 번역을 지원하나요?',
      },
    ],
    topic: FaqFilterType.how,
  },
];

/** Русская версия вопросов и ответов (AI-перевод 2026-09-17) */
const faqQuestionsAndAnswersRu: TopicQuestionAndAnswers[] = [
  {
    content: [
      {
        answer:
          'Нажмите любую кнопку «Начать» или кнопку функции на сайте, чтобы перейти на страницу регистрации. Зарегистрируйтесь по номеру телефона с кодом подтверждения — и получите доступ к переводу изображений, переводу текста, облачному хранилищу изображений, наложению водяных знаков, удалению текста с изображений и другим функциям. Всё просто, а при регистрации вы получаете большой объём бесплатных квот!',
        id: 'how-to-register',
        question: 'Как зарегистрироваться?',
      },
      {
        answer: (
          <span>
            При переводе текста Xiangji использует сторонние машинные переводчики. Для повышения точности мы подключили
            Papago (рекомендуется для пары китайский–корейский), Google Translate (для английского и языков латиницей),
            Alibaba Translation (редакция для электронной торговли) и DeepL (для менее распространённых языков).
          </span>
        ),
        id: 'how-are-coins-minted',
        isTop: true,
        question: 'Поддерживает ли Xiangji Google Translate?',
      },
    ],
    topic: FaqFilterType.how,
  },
];

/** 按当前语言选择问答内容（i18n，zh 兜底） */
export const useFaqQuestionsAndAnswers = (): TopicQuestionAndAnswers[] => {
  const {i18n} = useTranslation('faq');
  const byLang: Record<string, TopicQuestionAndAnswers[]> = {
    en: faqQuestionsAndAnswersEn,
    ko: faqQuestionsAndAnswersKo,
    ru: faqQuestionsAndAnswersRu,
  };
  return byLang[i18n.language] ?? faqQuestionsAndAnswersZh;
};
